import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Signup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ dealership_name: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Set up your dealership workspace in seconds"
      footer={<>Already have an account? <Link to="/login" className="text-gold font-semibold hover:underline" data-testid="go-login-link">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-2">
          <Label htmlFor="dealership">Dealership Name</Label>
          <Input id="dealership" required value={form.dealership_name} onChange={set("dealership_name")} data-testid="signup-dealership-input" placeholder="Apex Marine Group" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Your Name</Label>
          <Input id="name" required value={form.name} onChange={set("name")} data-testid="signup-name-input" placeholder="Jane Dealer" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Work Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="signup-email-input" placeholder="you@dealership.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="signup-password-input" placeholder="At least 6 characters" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="signup-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-navy hover:bg-navy-light text-white h-11 text-base" data-testid="signup-submit-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
        </Button>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
        <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-3 text-muted-foreground">or</span></div>
      </div>

      <Button type="button" variant="outline" onClick={googleLogin} className="w-full h-11 border-2" data-testid="google-signup-button">
        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5 mr-2" />
        Continue with Google
      </Button>
    </AuthShell>
  );
}
